import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import AuthLayout from "../components/AuthLayout";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

export default function ForgotPassword() {
  const { resetPassword } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      toast({
        title: "Couldn't send reset link",
        description: err?.message?.replace("Firebase: ", "") || "Please check the email and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      variant="customer"
      title={sent ? "Check your inbox" : "Reset your password"}
      subtitle={
        sent
          ? "If an account exists for that email, you'll receive a reset link within a minute."
          : "Enter your account email and we'll send you a link to reset your password."
      }
      footer={
        <>
          Remembered it?{" "}
          <Link to="/login" className="text-[#f59321] font-semibold hover:underline">
            Back to sign in
          </Link>
        </>
      }
    >
      {sent ? (
        <div className="text-center py-4">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <p className="text-slate-600">
            We sent the reset instructions to <span className="font-semibold text-[#0f2847]">{email}</span>.
          </p>
          <Button
            onClick={() => setSent(false)}
            variant="outline"
            className="mt-6 rounded-full h-11 px-5 font-semibold"
          >
            Use another email
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-[#0f2847] font-semibold">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="h-12 pl-10 rounded-xl border-slate-200 focus:border-[#f59321] focus:ring-[#f59321]"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="btn-lift w-full bg-[#f59321] hover:bg-[#e07f10] text-white font-semibold rounded-full h-12 shadow-md disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send reset link"}
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
